import React from 'react';
import { useIntl } from 'react-intl';

import {
    Breadcrumb as BreadcrumbDesignSystem,
    BreadcrumbProps as BreadcrumbPropsDesignSystem,
} from '../../../design-system/components/breadcrumb/Breadcrumb';
import { Author } from '../../../resources/author/domain/types/author';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { DestinationArticle } from '../../../resources/destinationArticles/domain/types/destinationArticle';
import { Facet } from '../../../resources/facet/domain/types/facet';
import { Itinerary } from '../../../resources/itinerary/domain/types/Itinerary';
import { PointOfInterest } from '../../../resources/pointOfInterest/domain/types/PointOfInterest';
import { Region } from '../../../resources/region/domain/types/region';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

export type BreadcrumbProps = Pick<BreadcrumbPropsDesignSystem, 'items' | 'reversed' | 'className'>;

export function Breadcrumb({ items: itemsProps, reversed, className }: BreadcrumbProps) {
    const { homeItem } = useHomeBreadcrumbItem();
    const items = itemsProps.length === 1 ? [homeItem, ...itemsProps] : itemsProps;

    return <BreadcrumbDesignSystem items={items} reversed={reversed} className={className} />;
}

export function useDestinationBreadcrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { homeItem } = useHomeBreadcrumbItem();
    const { destinationItem } = useDestinationBreadcrumbItem({ destination });

    return {
        breadcrumbItems: [homeItem, destinationItem],
    };
}

export function useAgencyBreadcrumbItems({
    destination,
    agencyName,
    agencyDescription,
    agencyId,
    agencySlug,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
    agencyName: string;
    agencyDescription: string;
    agencyId: number;
    agencySlug: string;
}) {
    const { breadcrumbItems } = useAgencyListBreadcrumbItems({ destination });
    const { generateUrl } = useUrlGenerator();

    const agencyNameItem = {
        text: `${agencyName.trim()}, ${agencyDescription}`,
        url: generateUrl('agency', {
            destinationSlug: destination.slug,
            agencyId: agencyId.toString(),
            agencySlug,
        }),
    };

    return {
        breadcrumbItems: breadcrumbItems.concat(agencyNameItem),
    };
}

export function useItinerariesBreadcrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { itinerariesItem } = useItinerariesBreadcrumbItem({ destination });
    const { breadcrumbItems } = useDestinationBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(itinerariesItem),
    };
}

export function useItineraryBreadcrumbItems({
    destination,
    itinerary,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
    itinerary: PickDomain<Itinerary, 'id' | 'slug' | 'title'>;
}) {
    const { homeItem } = useHomeBreadcrumbItem();
    const { generateUrl } = useUrlGenerator();

    const destinationItem = {
        text: destination.name,
        url: generateUrl('destination', { destinationSlug: destination.slug }) + '#Breadcrumb',
    };

    const { itinerariesItem } = useItinerariesBreadcrumbItem({ destination });
    const { itineraryItem } = useItineraryBreadcrumbItem({ destination, itinerary });

    return {
        breadcrumbItems: [homeItem, destinationItem, itinerariesItem, itineraryItem],
    };
}

export function useFacetBreadcrumbItems({
    destination,
    facet,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
    facet: PickDomain<Facet, 'slug' | 'title'>;
}) {
    const { facetItem } = useFacetBreadcrumbItem({ destination, facet });
    const { breadcrumbItems } = useDestinationBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(facetItem),
    };
}

export function useDestinationTripReviewsBreadcrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { destinationTripReviewsItem } = useDestinationTripReviewsBreadcrumbItem({ destination });
    const { breadcrumbItems } = useDestinationBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(destinationTripReviewsItem),
    };
}

export function useTripReviewsBreadcrumbItems() {
    const { tripReviewsItem } = useTripReviewsBreadcrumbItem();
    const { homeItem } = useHomeBreadcrumbItem();

    return {
        breadcrumbItems: [homeItem, tripReviewsItem],
    };
}

export function useTravelGuideBreadcrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { travelGuideItem } = useTravelGuideBreadcrumbItem({ destination });
    const { breadcrumbItems } = useDestinationBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(travelGuideItem),
    };
}

export function usePointOfInterestListBreadcrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { pointOfInterestListItem } = usePointOfInterestListBreadcrumbItem({ destination });
    const { breadcrumbItems } = useDestinationBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(pointOfInterestListItem),
    };
}

export function usePointOfInterestBreadcrumbItems({
    destination,
    pointOfInterest,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
    pointOfInterest: PickDomain<PointOfInterest, 'id' | 'slug' | 'name'>;
}) {
    const { pointOfInterestItem } = usePointOfInterestBreadcrumbItem({
        destination,
        pointOfInterest,
    });
    const { breadcrumbItems } = usePointOfInterestListBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(pointOfInterestItem),
    };
}

export function useRegionBreadcrumbItems({
    destination,
    region,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
    region: PickDomain<Region, 'name' | 'slug'>;
}) {
    const { regionItem } = useRegionBreadcrumbItem({
        destination,
        region,
    });
    const { breadcrumbItems } = useDestinationBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(regionItem),
    };
}

export function useRegionWhenToGoBreadcrumbItems({
    region,
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
    region: PickDomain<Region, 'name' | 'slug' | 'preposition'>;
}) {
    const { generateUrl } = useUrlGenerator();

    const { formatMessage } = useIntl();

    const { breadcrumbItems } = useRegionBreadcrumbItems({
        region,
        destination,
    });

    const whenToGoItem = {
        text: formatMessage(
            {
                id: 'page.regionWhenToGo.pageTitle',
            },
            { regionPreposition: region.preposition, regionName: region.name },
        ),
        url: generateUrl('region_when_to_go', {
            destinationSlug: destination.slug,
            regionSlug: region.slug,
        }),
    };

    return {
        breadcrumbItems: breadcrumbItems.concat(whenToGoItem),
    };
}

export function useHomeBreadcrumbItem() {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const homeItem = {
        text: formatMessage({ id: 'global.component.breadcrumb.home' }),
        url: generateUrl('home') + '#Breadcrumb',
        dataTestId: 'BreadcrumbItem-home',
    };

    return { homeItem };
}

function useDestinationBreadcrumbItem({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const destinationItem = {
        text: formatMessage(
            { id: 'global.component.breadcrumb.destination' },
            {
                destinationPreposition: destination.preposition,
                destinationName: destination.name,
            },
        ),
        url:
            generateUrl('destination', {
                destinationSlug: destination.slug,
            }) + '#Breadcrumb',
    };

    return { destinationItem };
}

export function useAgencyListBreadcrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { breadcrumbItems } = useDestinationBreadcrumbItems({ destination });

    const { formatMessage } = useIntl();
    const { generateUrl } = useUrlGenerator();

    const agencyListItem = {
        text: formatMessage(
            { id: 'global.component.breadcrumb.agencyList' },
            { destinationPreposition: destination.preposition, destinationName: destination.name },
        ),
        url: generateUrl('agency_list', {
            destinationSlug: destination.slug,
        }),
    };

    return {
        breadcrumbItems: breadcrumbItems.concat(agencyListItem),
    };
}

export function useDestinationWhenToGoBreadCrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();

    const { breadcrumbItems } = useTravelGuideBreadcrumbItems({ destination });

    const destinationWhenToGoItem = {
        text: formatMessage(
            {
                id: 'page.destinationWhenToGo.page.title',
            },
            { destinationPreposition: destination.preposition, destinationName: destination.name },
        ),
        url: generateUrl('destination_when_to_go', {
            destinationSlug: destination.slug,
        }),
    };

    return {
        breadcrumbItems: breadcrumbItems.concat(destinationWhenToGoItem),
    };
}

export function useDestinationSuitcaseBreadCrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();

    const destinationSuitcaseItem = {
        text: formatMessage(
            { id: 'page.destinationSuitcase.page.title' },
            { destinationPreposition: destination.preposition, destinationName: destination.name },
        ),
        url: generateUrl('destination_suitcase', {
            destinationSlug: destination.slug,
        }),
    };

    const { breadcrumbItems } = useTravelGuideBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(destinationSuitcaseItem),
    };
}

export function useDestinationVocabularyBreadCrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();

    const destinationVocabularyItem = {
        text: formatMessage(
            { id: 'page.destinationVocabulary.title' },
            {
                destinationPreposition: destination.preposition,
                destinationName: destination.name,
            },
        ),
        url: generateUrl('destination_vocabulary', {
            destinationSlug: destination.slug,
        }),
    };

    const { breadcrumbItems } = useTravelGuideBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(destinationVocabularyItem),
    };
}

export function useDestinationArticleListBreadcrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { formatMessage } = useIntl();
    const { generateUrl } = useUrlGenerator();

    const destinationArticlesItem = {
        text: formatMessage({ id: 'global.component.breadcrumb.destinationArticleList' }),
        url: generateUrl('destination_article_list', {
            destinationSlug: destination.slug,
        }),
    };

    const { breadcrumbItems } = useTravelGuideBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(destinationArticlesItem),
    };
}

export function useDestinationPracticalInformationArticleBreadcrumbItems({
    destination,
    destinationPracticalInformation,
}: {
    destination: PickDomain<Destination, 'slug' | 'name' | 'preposition'>;
    destinationPracticalInformation: PickDomain<
        DestinationArticle,
        'title' | 'documentId' | 'slug'
    >;
}) {
    const { generateUrl } = useUrlGenerator();

    const destinationPracticalInformationArticleItem = {
        text: destinationPracticalInformation.title,
        url: generateUrl('destination_practical_information_article', {
            destinationSlug: destination.slug,
            articleDocumentId: destinationPracticalInformation.documentId,
            articleSlug: destinationPracticalInformation.slug,
        }),
    };

    const { breadcrumbItems } = useDestinationArticleListBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(destinationPracticalInformationArticleItem),
    };
}

export function useDestinationInspirationArticleBreadcrumbItems({
    destination,
    destinationInspirationArticle,
}: {
    destination: PickDomain<Destination, 'slug' | 'name' | 'preposition'>;
    destinationInspirationArticle: PickDomain<DestinationArticle, 'title' | 'documentId' | 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();

    const destinationInspirationArticleItem = {
        text: destinationInspirationArticle.title,
        url: generateUrl('destination_inspiration_article', {
            destinationSlug: destination.slug,
            articleDocumentId: destinationInspirationArticle.documentId,
            articleSlug: destinationInspirationArticle.slug,
        }),
    };

    const { breadcrumbItems } = useDestinationArticleListBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(destinationInspirationArticleItem),
    };
}

export function useDestinationBudgetBreadcrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();

    const destinationBudgetItem = {
        text: formatMessage({ id: 'global.component.breadcrumb.destinationBudget' }),
        url: generateUrl('destination_budget', {
            destinationSlug: destination.slug,
        }),
    };

    const { breadcrumbItems } = useTravelGuideBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(destinationBudgetItem),
    };
}

export function useDestinationVisaBreadcrumbItems({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();

    const destinationVisaItem = {
        text: formatMessage(
            { id: 'page.destinationVisa.title' },
            {
                destinationPreposition: destination.preposition,
                destinationName: destination.name,
            },
        ),
        url: generateUrl('destination_visa', {
            destinationSlug: destination.slug,
        }),
    };

    const { breadcrumbItems } = useTravelGuideBreadcrumbItems({ destination });

    return {
        breadcrumbItems: breadcrumbItems.concat(destinationVisaItem),
    };
}

function useItinerariesBreadcrumbItem({
    destination,
}: {
    destination: PickDomain<Destination, 'name' | 'slug'>;
}) {
    const { formatMessage } = useIntl();
    const { generateUrl } = useUrlGenerator();
    const itinerariesItem = {
        text: formatMessage(
            { id: 'global.component.breadcrumb.itineraries' },
            {
                destinationName: destination.name,
            },
        ),
        url: generateUrl('itinerary_list', {
            destinationSlug: destination.slug,
        }),
    };

    return { itinerariesItem };
}

function useItineraryBreadcrumbItem({
    destination,
    itinerary,
}: {
    destination: PickDomain<Destination, 'slug'>;
    itinerary: PickDomain<Itinerary, 'id' | 'slug' | 'title'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const itineraryItem = {
        text: itinerary.title,
        url: generateUrl('itinerary', {
            destinationSlug: destination.slug,
            itineraryId: itinerary.id,
            itinerarySlug: itinerary.slug,
        }),
    };

    return { itineraryItem };
}

function useFacetBreadcrumbItem({
    destination,
    facet,
}: {
    destination: PickDomain<Destination, 'slug'>;
    facet: PickDomain<Facet, 'slug' | 'title'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const facetItem = {
        text: facet.title,
        url: generateUrl('facet', {
            destinationSlug: destination.slug,
            facetSlug: facet.slug,
        }),
    };

    return { facetItem };
}

function useDestinationTripReviewsBreadcrumbItem({
    destination,
}: {
    destination: PickDomain<Destination, 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const destinationTripReviewsItem = {
        text: formatMessage({ id: 'global.component.breadcrumb.tripReviews' }),
        url: generateUrl('trip_review_list_destination', {
            destinationSlug: destination.slug,
        }),
    };

    return { destinationTripReviewsItem };
}

function useTripReviewsBreadcrumbItem() {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const tripReviewsItem = {
        text: formatMessage({ id: 'global.component.breadcrumb.tripReviews' }),
        url: generateUrl('trip_review_list'),
    };

    return { tripReviewsItem };
}

export function useWhereToGoBreadcrumbItem() {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const whereToGoItem = {
        text: formatMessage({ id: 'global.component.breadcrumb.whereToGo' }),
        url: generateUrl('where_to_go'),
    };

    return { whereToGoItem };
}

export function useDestinationListBreadcrumbItem() {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const destinationListItem = {
        text: formatMessage({ id: 'global.component.breadcrumb.destinationList' }),
        url: generateUrl('destination_list'),
    };

    return { destinationListItem };
}

function useTravelGuideBreadcrumbItem({
    destination,
}: {
    destination: PickDomain<Destination, 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const travelGuideItem = {
        text: formatMessage({ id: 'global.component.breadcrumb.travelGuide' }),
        url: generateUrl('travel_guide', {
            destinationSlug: destination.slug,
        }),
    };

    return { travelGuideItem };
}

function usePointOfInterestListBreadcrumbItem({
    destination,
}: {
    destination: PickDomain<Destination, 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const pointOfInterestListItem = {
        text: formatMessage({ id: 'global.component.breadcrumb.pointOfInterestList' }),
        url: generateUrl('point_of_interest_list', {
            destinationSlug: destination.slug,
        }),
    };

    return { pointOfInterestListItem };
}

function usePointOfInterestBreadcrumbItem({
    destination,
    pointOfInterest,
}: {
    destination: PickDomain<Destination, 'slug'>;
    pointOfInterest: PickDomain<PointOfInterest, 'id' | 'slug' | 'name'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const pointOfInterestItem = {
        text: pointOfInterest.name,
        url: generateUrl('place', {
            destinationSlug: destination.slug,
            placeId: pointOfInterest.id,
            placeSlug: pointOfInterest.slug,
        }),
    };

    return { pointOfInterestItem };
}

function useRegionBreadcrumbItem({
    region,
    destination,
}: {
    region: PickDomain<Region, 'name' | 'slug'>;
    destination: PickDomain<Destination, 'slug'>;
}) {
    const { generateUrl } = useUrlGenerator();
    const regionItem = {
        text: region.name,
        url: generateUrl('region', {
            destinationSlug: destination.slug,
            regionSlug: region.slug,
        }),
    };

    return { regionItem };
}

export function useAuthorListBreadcrumbItem() {
    const { homeItem } = useHomeBreadcrumbItem();

    const { generateUrl } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const authorListItem = {
        text: formatMessage({ id: 'global.authorBreadcrumb' }),
        url: generateUrl('author_list'),
    };

    return { breadcrumbItems: [homeItem, authorListItem] };
}

export function useAuthorBreadcrumbItem({
    author,
}: {
    author: PickDomain<Author, 'id' | 'slug' | 'firstName' | 'lastName'>;
}) {
    const { generateUrl } = useUrlGenerator();

    const authorItem = {
        text: `${author.firstName} ${author.lastName}`,
        url: generateUrl('author', {
            authorId: author.id,
            authorSlug: author.slug,
        }),
    };

    const { breadcrumbItems } = useAuthorListBreadcrumbItem();

    return { breadcrumbItems: breadcrumbItems.concat(authorItem) };
}
